import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  useTheme,
  Tab,
  Tabs,
  alpha,
  InputAdornment,
  Button,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Chip
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getImagesPublic } from 'src/apiCalls';
import { Image, ImageFilters } from "../../../../interfaces";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PaidIcon from '@mui/icons-material/Paid';
import logger from 'src/logger';
import Logo from 'src/components/LogoSign';

const BrowseImagesPage = () => {
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const { userId } = useParams();
  const [speciesFilter, setSpeciesFilter] = useState('');
  const [showLocationOnly, setShowLocationOnly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const theme = useTheme();

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedCategory(newValue);
  };

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const filters: ImageFilters = {};
        if (speciesFilter.trim()) filters.species = speciesFilter.trim();
        if (showLocationOnly) filters.locationOnly = true;
        if (selectedCategory !== 'all') filters.category = selectedCategory;
        if (userId) filters.userId = userId;
        const data = await getImagesPublic(filters);
        setImages(data.images || []);
      } catch (error) {
        logger.error('Error fetching images:', error);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchImages, 300);
    return () => clearTimeout(debounceTimer);
  }, [speciesFilter, showLocationOnly, selectedCategory, userId]);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        pb: 2
      }}
    >
      <Container
        maxWidth="xl"
        disableGutters
        sx={{
          position: 'relative',
          zIndex: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          width: '100%',
          px: { xs: 1, sm: 2 }
        }}
      >
        {/* Category Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: theme.palette.divider, mb: 1.5 }}>
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{
              style: {
                backgroundColor: theme.palette.primary.dark,
                height: 2,
                opacity: 0.7,
              }
            }}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 0,
                minHeight: 36,
                padding: { xs: '2px 6px', sm: '4px 10px' },
                fontSize: { xs: '0.8rem', sm: '0.95rem' },
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.dark,
                  fontWeight: 'bold',
                  opacity: 1,
                  zIndex: 1,
                  backgroundColor: 'transparent',
                },
                '&:hover': {
                  color: theme.palette.primary.light,
                  opacity: 1,
                },
              },
              '& .MuiTabs-flexContainer': {
                justifyContent: { xs: 'flex-start', sm: 'center' },
              },
              overflowX: { xs: 'auto', sm: 'hidden' },
              '& .MuiTabs-scrollButtons': {
                display: { xs: 'flex', sm: 'none' },
                color: theme.palette.primary.main,
              },
            }}
          >
            <Tab label="All" value="all" />
            <Tab label="Sea" value="sea" />
            <Tab label="Predator" value="predator" />
            <Tab label="Coarse" value="coarse" />
            <Tab label="Carp" value="carp" />
          </Tabs>
        </Box>

        {/* Search Controls */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'row' },
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          px: 1,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, 0.07),
          borderRadius: 0.5
        }}>
          <TextField
            size="small"
            placeholder="Search by species..."
            value={speciesFilter}
            onChange={(e) => setSpeciesFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: { xs: '140px', sm: '200px' },
              '& .MuiInputBase-root': {
                height: 36,
                backgroundColor: theme.palette.background.paper,
                borderRadius: 0.5,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.6),
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                }
              }
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showLocationOnly}
                onChange={(e) => setShowLocationOnly(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body2">Location only</Typography>
            }
            sx={{ 
              m: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem'
              }
            }}
          />
        </Box>

        {/* Images Grid */}
        <Grid container spacing={1.5}>
          {loading ? (
            <Grid item xs={12}>
              <Typography align="center" sx={{ py: 2 }}>Loading...</Typography>
            </Grid>
          ) : images.length === 0 ? (
            <Grid item xs={12}>
              <Typography align="center" sx={{ py: 2 }}>No catches found</Typography>
            </Grid>
          ) : (
            images.map((image) => (
              <Grid item key={image.id} xs={6} sm={4} md={3} lg={3}>
                <RouterLink to={`/image/${image.id}`} style={{ textDecoration: 'none' }}>
                  <Box sx={{
                    position: 'relative',
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 1,
                    overflow: 'hidden',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                    border: `1px solid ${theme.palette.divider}`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                    }
                  }}>
                    <Box
                      component="img"
                      src={image.path}
                      alt="Catch"
                      sx={{
                        width: '100%',
                        height: { xs: '130px', sm: '160px' },
                        objectFit: 'cover',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    />
                    <Box sx={{
                      p: { xs: 1, sm: 1.5 },
                      background: `linear-gradient(to bottom, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.light, 0.07)})`,
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {image.species && (
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: '600',
                            color: theme.palette.text.primary,
                            mb: 0.5,
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {image.species}
                        </Typography>
                      )}
                      {image.tags && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            mb: 0.5,
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {image.tags}
                        </Typography>
                      )}
                      
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'flex-start', 
                        mt: 'auto',
                        flexWrap: 'wrap'
                      }}>
                        {image.weight && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.text.secondary,
                              fontSize: { xs: '0.75rem', sm: '0.8rem' }, 
                              mb: 0.5,
                              flexBasis: '100%'
                            }}
                          >
                            <strong>Weight:</strong> {image.weight}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Location and Free Indicators */}
                    {image.publicise && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          display: 'flex',
                          gap: 0.5,
                          alignItems: 'center',
                        }}
                      >
                        {/* Location Indicator */}
                        <Box
                          sx={{
                            backgroundColor: alpha(theme.palette.background.paper, 0.8),
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '2px 4px',
                            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
                          }}
                        >
                          <LocationOnIcon sx={{ mr: 0.5, fontSize: '0.9rem', color: theme.palette.primary.main }} />
                          <Typography
                            variant="caption"
                            sx={{ 
                              color: theme.palette.primary.main, 
                              fontWeight: 'bold',
                              fontSize: '0.65rem'
                            }}
                          >
                            Location
                          </Typography>
                        </Box>

                        {/* Free Location Indicator */}
                        {!image.chargeForLocation && (
                          <Box
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.8),
                              borderRadius: '4px',
                              padding: '2px 4px',
                              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ 
                                color: theme.palette.common.white, 
                                fontWeight: 'bold',
                                fontSize: '0.65rem'
                              }}
                            >
                              Free
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </RouterLink>
              </Grid>
            ))
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default BrowseImagesPage; 